import * as React from 'react';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {useEffect, useState} from "react";
import projects from '../../models/projects'
import helpers from "../../helpers/common";
import useRootHook from "../../hooks/useRoot";
import {Add, Check, FormatListBulleted, People, Delete} from "@mui/icons-material";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import {Tooltip, ToggleButton, ToggleButtonGroup} from "@mui/material";
import {AclRouterLink} from "../ACL";
import {Link as RouterLink, useSearchParams} from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import PageHeader from '../layout/PageHeader';
import SectionCard from '../layout/SectionCard';
import TableToolbar from '../layout/TableToolbar';
import Pagination from '../Pagination';

export default function ProjectIndex() {
    const [searchParams, setSearchParams] = useSearchParams()
    const [projectsData, setProjectsData] = useState(null)
    const [refreshId, setRefreshId] = useState(0)
    const {setShowLoader, userData, showConfirm} = useRootHook()
    
    const enabledFilter = searchParams.get('enabled') || 'all'
    
    const handleEnabledFilterChange = (event, newValue) => {
        if (newValue === null) return
        const newParams = new URLSearchParams(searchParams)
        if (newValue === 'all') {
            newParams.delete('enabled')
        } else {
            newParams.set('enabled', newValue === 'active' ? 'true' : 'false')
        }
        setSearchParams(newParams)
    }

    useEffect(() => {
        setShowLoader(true)
        projects.index(searchParams ? Object.fromEntries(searchParams) : {})
            .then((data) => {
                data && setProjectsData(data)
                setShowLoader(false)
            }).catch(() => setShowLoader(false))
    }, [searchParams, refreshId])

    const handleDelete = async (id) => {
        const confirmed = await showConfirm('Are you sure you want to delete this project? This will also delete related action logs and payment method settings.', 'Delete Project');
        if (confirmed) {
            setShowLoader(true);
            projects.delete(id)
                .then(() => {
                    setShowLoader(false);
                    setRefreshId((prevId) => prevId + 1);
                })
                .catch(() => setShowLoader(false));
        }
    };

    if (!projectsData) return null

    return <React.Fragment>
        <Grid size={12}>
            <PageHeader
                title="Projects"
                breadcrumbs={[['Dashboard', '/'], ['Projects', '/projects']]}
                actions={
                    <Button to="/projects/add" component={AclRouterLink} variant="contained" startIcon={<Add />}>
                        Add Project
                    </Button>
                }
            />
        </Grid>
        <Grid size={12}>
            <SectionCard sx={{ display: 'flex', flexDirection: 'column', overflowX: 'auto'}}>
                <TableToolbar 
                    title="Projects" 
                    actions={
                        <ToggleButtonGroup
                            value={enabledFilter}
                            size="small"
                            exclusive
                            onChange={handleEnabledFilterChange}
                            aria-label="Status filter"
                        >
                            <ToggleButton value="all">ALL</ToggleButton>
                            <ToggleButton value="active">Active</ToggleButton>
                            <ToggleButton value="inactive">Inactive</ToggleButton>
                        </ToggleButtonGroup>
                    }
                />
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Name</TableCell>
                            {userData.role !== helpers.roles.M && <TableCell>Merchant</TableCell>}
                            <TableCell>URL</TableCell>
                            <TableCell>Enabled</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell align="right"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {projectsData.projects.map((row) => (
                            <TableRow key={`pro-${row.id}`}>
                                <TableCell>
                                    {row.id}
                                </TableCell>
                                <TableCell>
                                    <Link to={`/projects/edit?uuid=${helpers.UUIDv4Transform(row.uuid)}`} component={AclRouterLink}>
                                        {row.name}
                                    </Link>
                                </TableCell>
                                {userData.role !== helpers.roles.M && <TableCell>{row.merchant.name}</TableCell>}
                                <TableCell>
                                    {row.url}
                                </TableCell>
                                <TableCell>{!!row.enabled ? <Check fontSize="small" /> : ''}</TableCell>
                                <TableCell>{helpers.getDate(row.created)}</TableCell>
                                <TableCell align="right">
                                    <Tooltip title="View transactions">
                                        <IconButton to={`/transactions?project_id=${row.id}`} component={RouterLink}>
                                            <FormatListBulleted fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="View customers">
                                        <IconButton to={`/customers?project_id=${row.id}`} component={RouterLink}>
                                            <People fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    {userData?.role === helpers.roles.S && (
                                        <Tooltip title="Delete project">
                                            <IconButton onClick={() => handleDelete(row.id)} color="error" size="small">
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <Pagination url="/projects" count={projectsData.count} />
            </SectionCard>
        </Grid>
    </React.Fragment>
}

